/**
 * EHR Tasks API — backend calls for the EHR staged-task approval page.
 *
 * Read:   getresidentstaged        (all staged resident tasks: add, edit, move, remove)
 *         getusersstaged           (all staged user tasks: add, remove)
 *         core_resident_groups     (groups for the move-in group picker)
 * Write:  resident_add_task        (approve move-in)
 *         resident_move_task       (approve relocation)
 *         resident_remove_task     (approve move-out)
 *         resident_reject_task     (reject any resident task type)
 *         user_reject_task         (reject any user task type)
 *
 * User add/delete approvals route through the existing usersApi.addUser /
 * deleteUsers with taskApproval=true (action: "new-task" / "remove-task"),
 * which clears the matching ext_stg_user row server-side.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all staged resident tasks from the EHR.
 *
 * V1 procedure: getresidentstaged (stored)
 *
 * Returns an array of 4 arrays:
 *   [0] = add (move-in) tasks
 *   [1] = edit (update info) tasks: approved automatically, not shown (4.3.11)
 *   [2] = move (relocate) tasks
 *   [3] = remove (move-out) tasks
 */
export function fetchStagedTasks(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getresidentstaged',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Fetch all resident groups (for the group picker in the add-task dialog).
 *
 * V1: Tbl: "core_resident_groups", procedure: "fetchbyparameter", reqType: "direct"
 */
export function fetchResidentGroups(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_resident_groups',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all staged user tasks from the EHR.
 *
 * V1 procedure: getusersstaged (stored)
 *
 * Returns an array of 2 arrays:
 *   [0] = add user tasks
 *   [1] = remove user tasks
 */
export function fetchStagedUsers(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getusersstaged',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Fetch the decided EHR tasks for History: residents and users in one list, newest first.
 *
 * mysql_fetch.py `getehrtaskhistory` → sp_getEhrTaskHistory + sp_getEhrUserTaskHistory
 * (docs/EHR_TASK_HISTORY_PLAN.md §6). The window is half-open, in UTC, at most 366 days;
 * nothing before 2026-10-06 19:00 UTC is returned (decision 13).
 *
 * @param {number} campusId
 * @param {string} startUtc — 'YYYY-MM-DD HH:MM:SS'
 * @param {string} endUtc — 'YYYY-MM-DD HH:MM:SS'
 * @returns {Promise<object[]>} rows of { row_key, decided_at, subject_type, task_type, outcome,
 *   entity_id, subject_name, subject_exists, actor_id, actor_name, cloud_id, vendor_id, reason,
 *   received_at, changes, trunc, proposed }
 */
export function fetchTaskHistory(campusId, startUtc, endUtc) {
  return apiFetch({
    procedure: 'getehrtaskhistory',
    reqType: 'stored',
    payload: { campus_id: campusId, start_utc: startUtc, end_utc: endUtc },
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Approve a move-in (add) task.
 *
 * V1 TransType: resident_add_task
 *
 * @param {object} data — full task payload
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function approveAddTask(data, session) {
  return apiTransaction({
    transType: 'resident_add_task',
    payload: [data],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Approve a resident relocation (move) task.
 *
 * V1 TransType: resident_move_task
 */
export function approveMoveTask(data, oldData, session) {
  return apiTransaction({
    transType: 'resident_move_task',
    payload: [data],
    oldPayload: [oldData],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Approve a move-out (remove) task.
 *
 * V1 TransType: resident_remove_task
 */
export function approveRemoveTask(data, session) {
  return apiTransaction({
    transType: 'resident_remove_task',
    payload: [data],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Reject any staged task (add / edit / move / remove).
 *
 * V1 TransType: resident_reject_task
 *
 * @param {string} cloudId — the cloud_id of the task
 * @param {object} session — { campusId, userId, clientId }
 * @param {string} rejectReason — required; saved on the staged row for EHR task History
 *   (docs/EHR_TASK_HISTORY_PLAN.md §5.2). The Lambda refuses an empty reason.
 */
export function rejectTask(cloudId, session, rejectReason) {
  return apiTransaction({
    transType: 'resident_reject_task',
    payload: [{ cloud_id: cloudId, modified_by: session.userId, reject_reason: rejectReason }],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Reject any staged user task (add / remove).
 *
 * V1 TransType: user_reject_task — clears the matching ext_stg_user row.
 *
 * @param {string} cloudId — the cloud_id of the staged user task
 * @param {object} session — { campusId, userId, clientId }
 * @param {string} rejectReason — required, as for rejectTask
 */
export function rejectUserTask(cloudId, session, rejectReason) {
  return apiTransaction({
    transType: 'user_reject_task',
    payload: [{ cloud_id: cloudId, modified_by: session.userId, reject_reason: rejectReason }],
    oldPayload: [],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
